import { useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, Circle, Upload, Send, FileText } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import {
  bookingsApi,
  type WorkflowAttachmentWithMeta,
  type WorkflowChecklistRequirement,
  type BookingImage,
} from '../../services/bookingsApi';
import { getApiBase } from '../../lib/apiBase';
import { compressImages } from '../../lib/imageCompression';

interface WorkflowChecklistSectionProps {
  bookingId: string;
  jobId?: string | null;
  onUpdated?: () => void;
  showAttachmentPreviews?: boolean;
}

function normalizeTypeLabel(type: WorkflowChecklistRequirement['config']['itemType']): string {
  if (type === 'boolean') return 'checkbox';
  if (type === 'text' || type === 'textarea') return 'text';
  if (type === 'photo') return 'file';
  return type;
}

function formatRelativeTime(timestamp: number | null | undefined): string {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;

  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function WorkflowChecklistSection({ bookingId, jobId, onUpdated, showAttachmentPreviews = true }: WorkflowChecklistSectionProps) {
  const { success, error } = useToast();
  const apiBase = getApiBase();

  const [items, setItems] = useState<WorkflowChecklistRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(true);
  const [isSaving, setIsSaving] = useState<string | null>(null);
  const [isSendingSignatureLink, setIsSendingSignatureLink] = useState<string | null>(null);
  const [resolvedJobId, setResolvedJobId] = useState<string | null>(jobId ?? null);

  const [draftValues, setDraftValues] = useState<Record<string, boolean | number | string | Record<string, unknown> | null>>({});

  const [workflowAttachmentsByItem, setWorkflowAttachmentsByItem] = useState<Record<string, WorkflowAttachmentWithMeta[]>>({});
  const [bookingImages, setBookingImages] = useState<BookingImage[]>([]);

  const loadChecklist = async () => {
    const data = await bookingsApi.getChecklist(bookingId);
    setItems(data);
  };

  const loadAssets = async () => {
    const [photoAttachments, signatureAttachments, fileAttachments, images] = await Promise.all([
      bookingsApi.getAttachments(bookingId, 'photo'),
      bookingsApi.getAttachments(bookingId, 'signature'),
      bookingsApi.getAttachments(bookingId, 'file'),
      bookingsApi.getImages(bookingId),
    ]);

    const grouped: Record<string, WorkflowAttachmentWithMeta[]> = {};
    [...photoAttachments, ...signatureAttachments, ...fileAttachments].forEach((att) => {
      if (!grouped[att.checklistItemId]) grouped[att.checklistItemId] = [];
      grouped[att.checklistItemId].push(att);
    });

    Object.values(grouped).forEach((list) => {
      list.sort((a, b) => (b.uploadedAt || 0) - (a.uploadedAt || 0));
    });

    setWorkflowAttachmentsByItem(grouped);
    setBookingImages(images);
  };

  const refreshData = async () => {
    await Promise.all([loadChecklist(), loadAssets()]);
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setIsLoadingAssets(true);
      try {
        await Promise.all([loadChecklist(), loadAssets()]);
      } catch {
        if (!cancelled) error('No se pudo cargar el checklist');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsLoadingAssets(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [bookingId, error]);

  useEffect(() => {
    if (jobId) {
      setResolvedJobId(jobId);
      return;
    }

    let cancelled = false;
    api.get<{ id: string }>(`/api/jobs/by-booking/${bookingId}`)
      .then((job) => {
        if (cancelled) return;
        setResolvedJobId(job.id);
      })
      .catch(() => {
        if (cancelled) return;
        setResolvedJobId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [bookingId, jobId]);

  const statusId = items[0]?.config.status;

  const bookingImageItemsByKey = useMemo(() => {
    const result: Record<string, BookingImage[]> = {};
    items.forEach((item) => {
      const source = ((item.config.validationRules as Record<string, unknown> | undefined)?.source as string | undefined) || undefined;
      if (item.config.itemType === 'photo' && source === 'booking_images') {
        result[item.config.itemKey] = bookingImages.filter((img) => img.contentType?.startsWith('image/'));
      }
    });
    return result;
  }, [bookingImages, items]);

  const saveValue = async (item: WorkflowChecklistRequirement, value: boolean | number | string | Record<string, unknown> | null) => {
    if (!statusId) return;
    setIsSaving(item.config.itemKey);
    try {
      await bookingsApi.saveChecklistValues(bookingId, statusId, [{ itemKey: item.config.itemKey, value }]);
      await refreshData();
      success('Checklist actualizado');
      onUpdated?.();
    } catch {
      error('No se pudo guardar el checklist');
    } finally {
      setIsSaving(null);
    }
  };

  const uploadChecklistFiles = async (item: WorkflowChecklistRequirement, files: File[]) => {
    if (!files.length) return;
    setIsSaving(item.config.itemKey);
    try {
      const optimizedFiles = item.config.itemType === 'photo' ? await compressImages(files) : files;
      const formData = new FormData();
      optimizedFiles.forEach((file) => formData.append('file', file));
      const response = await fetch(`${apiBase}/api/bookings/${bookingId}/checklist/${item.config.id}/attachments`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Upload failed');

      await refreshData();
      success('Archivo(s) subido(s)');
      onUpdated?.();
    } catch {
      error('No se pudo subir el archivo');
    } finally {
      setIsSaving(null);
    }
  };

  const uploadBookingImages = async (item: WorkflowChecklistRequirement, files: File[]) => {
    if (!files.length) return;
    setIsSaving(item.config.itemKey);
    try {
      const optimizedFiles = await compressImages(files);
      const formData = new FormData();
      optimizedFiles.forEach((file) => formData.append('image', file));
      const response = await fetch(`${apiBase}/api/bookings/${bookingId}/images`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Upload failed');

      await refreshData();
      success('Foto(s) subida(s)');
      onUpdated?.();
    } catch {
      error('No se pudo subir la(s) foto(s)');
    } finally {
      setIsSaving(null);
    }
  };

  const sendSignatureLink = async (itemKey: string) => {
    setIsSendingSignatureLink(itemKey);
    try {
      if (resolvedJobId) {
        await api.post(`/api/jobs/${resolvedJobId}/send-signature-link`, {});
      } else {
        // No job exists yet — fall back to booking-based endpoint.
        await api.post(`/api/bookings/${bookingId}/send-signature-link`, {});
      }
      success('Enlace de firma enviado');
    } catch (err) {
      error(err instanceof Error ? err.message : 'No se pudo enviar el enlace de firma');
    } finally {
      setIsSendingSignatureLink(null);
    }
  };

  if (isLoading) {
    return <div className="rounded-lg border p-4 text-sm text-gray-500">Cargando checklist…</div>;
  }

  if (items.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border p-4 space-y-3">
      <h3 className="font-medium text-gray-900">Checklist</h3>
      {items.map((item) => {
        const isBusy = isSaving === item.config.itemKey;
        const currentValue = draftValues[item.config.itemKey] ?? item.value;
        const isAttachment = ['photo', 'file', 'signature'].includes(item.config.itemType);
        const source = ((item.config.validationRules as Record<string, unknown> | undefined)?.source as string | undefined) || undefined;
        const isBookingImagesSource = item.config.itemType === 'photo' && source === 'booking_images';

        const workflowAttachments = workflowAttachmentsByItem[item.config.id] || [];
        const itemImages = bookingImageItemsByKey[item.config.itemKey] || [];
        const attachmentCount = isBookingImagesSource
          ? itemImages.length
          : workflowAttachments.length;

        return (
          <div key={item.config.itemKey} className="rounded-md border border-gray-200 p-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {item.config.itemLabel}
                </p>
                <p className="text-xs text-gray-500">
                  Tipo: {normalizeTypeLabel(item.config.itemType)} · {item.config.isRequired ? 'Obligatorio' : 'Opcional'}
                </p>
              </div>
              <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                {item.isComplete ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Circle className="h-3.5 w-3.5 text-gray-400" />}
                {item.isComplete ? 'Completo' : 'Pendiente'}
              </span>
            </div>

            {item.config.itemType === 'boolean' && (
              <label className="mt-2 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={currentValue === true}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setDraftValues((prev) => ({ ...prev, [item.config.itemKey]: checked }));
                    void saveValue(item, checked);
                  }}
                  disabled={isBusy}
                />
                Confirmado
              </label>
            )}

            {(item.config.itemType === 'text' || item.config.itemType === 'textarea') && (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm"
                  value={typeof currentValue === 'string' ? currentValue : ''}
                  onChange={(e) => setDraftValues((prev) => ({ ...prev, [item.config.itemKey]: e.target.value }))}
                  disabled={isBusy}
                />
                <button
                  type="button"
                  className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => void saveValue(item, typeof currentValue === 'string' ? currentValue : '')}
                  disabled={isBusy}
                >
                  {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Guardar'}
                </button>
              </div>
            )}

            {item.config.itemType === 'number' && (
              <div className="mt-2 flex gap-2">
                <input
                  type="number"
                  className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm"
                  value={typeof currentValue === 'number' || typeof currentValue === 'string' ? String(currentValue) : ''}
                  onChange={(e) => setDraftValues((prev) => ({ ...prev, [item.config.itemKey]: e.target.value }))}
                  disabled={isBusy}
                  placeholder="Introduce un valor"
                />
                <button
                  type="button"
                  className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => {
                    const numeric = Number(currentValue);
                    void saveValue(item, Number.isFinite(numeric) ? numeric : null);
                  }}
                  disabled={isBusy}
                >
                  {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Guardar'}
                </button>
              </div>
            )}

            {item.config.itemType === 'select' && (
              <div className="mt-2 flex gap-2">
                <select
                  className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm"
                  value={typeof currentValue === 'string' ? currentValue : ''}
                  onChange={(e) => setDraftValues((prev) => ({ ...prev, [item.config.itemKey]: e.target.value }))}
                  disabled={isBusy}
                >
                  <option value="">Selecciona una opción</option>
                  {(item.config.options || []).map((opt) => (
                    <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                  onClick={() => void saveValue(item, typeof currentValue === 'string' ? currentValue : '')}
                  disabled={isBusy}
                >
                  {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Guardar'}
                </button>
              </div>
            )}

            {!isAttachment && item.value !== null && item.value !== undefined && item.value !== '' && (
              <p className="mt-2 text-xs text-gray-600">
                Valor actual: <span className="font-medium text-gray-800">{typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}</span>
              </p>
            )}

            {isAttachment && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-600">Archivos: {attachmentCount}</p>

                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex items-center gap-2 text-xs px-2 py-1 border rounded cursor-pointer hover:bg-gray-50">
                    <Upload className="h-3.5 w-3.5" />
                    {isBookingImagesSource ? 'Subir foto' : 'Subir archivo'}
                    <input
                      type="file"
                      multiple
                      accept={item.config.itemType === 'photo' ? 'image/*' : undefined}
                      className="hidden"
                      onChange={(e) => {
                        const files = e.target.files ? Array.from(e.target.files) : [];
                        if (isBookingImagesSource) {
                          void uploadBookingImages(item, files);
                        } else {
                          void uploadChecklistFiles(item, files);
                        }
                        e.target.value = '';
                      }}
                      disabled={isBusy}
                    />
                  </label>

                  {item.config.itemType === 'signature' && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 text-xs px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => void sendSignatureLink(item.config.itemKey)}
                      disabled={isSendingSignatureLink === item.config.itemKey || !resolvedJobId}
                    >
                      {isSendingSignatureLink === item.config.itemKey ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Send className="h-3.5 w-3.5" />
                      )}
                      Enviar enlace de firma
                    </button>
                  )}
                </div>

                {showAttachmentPreviews && (
                  isLoadingAssets ? (
                    <p className="text-xs text-gray-500">Cargando adjuntos…</p>
                  ) : isBookingImagesSource ? (
                    itemImages.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {itemImages.map((img) => (
                          <a
                            key={img.id}
                            href={`${apiBase}/api/bookings/${bookingId}/images/${img.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded border border-gray-100 overflow-hidden"
                          >
                            <img
                              src={`${apiBase}/api/bookings/${bookingId}/images/${img.id}`}
                              alt={img.originalFilename || 'Foto checklist'}
                              className="h-20 w-full object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">Sin fotos</p>
                    )
                  ) : workflowAttachments.length > 0 ? (
                    <div className="space-y-2">
                      {workflowAttachments.map((att) => {
                        const fullUrl = `${apiBase}${att.url}`;
                        const isImage = att.category === 'photo' || att.category === 'signature' || att.contentType?.startsWith('image/');

                        return (
                          <div key={att.id} className="rounded border border-gray-100 p-2">
                            {isImage ? (
                              <a href={fullUrl} target="_blank" rel="noreferrer" className="block rounded overflow-hidden bg-gray-50">
                                <img src={fullUrl} alt={att.checklistItemLabel || 'Adjunto'} className="h-24 w-full object-contain" />
                              </a>
                            ) : (
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
                              >
                                <FileText className="h-4 w-4" />
                                Abrir archivo
                              </a>
                            )}
                            <p className="mt-1 text-[11px] text-gray-500">
                              {att.uploadedBy?.name || '—'}
                              {att.uploadedAt ? ` • ${formatRelativeTime(att.uploadedAt)}` : ''}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Sin archivos</p>
                  )
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
